import { X, CheckCircle2 } from 'lucide-react';
import { useProfile } from '../../../../../context/profile/ProfileContext';
import CustomSelect from '../../../../../components/forms/CustomSelect';
import subscriptionData from '../../../../../data/subscription/subscriptionData.json';

const downgradeReasonOptions = subscriptionData.modalReasonOptions?.downgrade || [];

const DowngradeModal = () => {
  const {
    showDowngradeModal,
    setShowDowngradeModal,
    selectedDowngradePlan,
    setSelectedDowngradePlan,
    requestReason,
    setRequestReason,
    requestComments,
    setRequestComments,
    isSubmittingRequest,
    requestSuccessMessage,
    setRequestSuccessMessage,
    handleDowngradeRequest,
    formatCurrency
  } = useProfile();

  if (!showDowngradeModal || !selectedDowngradePlan) return null;

  return (
    <div
      className="fixed inset-0 z-[99999] bg-black/50 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto"
      onWheel={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
    >
      <div className="bg-white rounded-3xl max-w-md w-full overflow-hidden shadow-2xl animate-modal-entry border border-slate-100">
        {requestSuccessMessage ? (
          <div className="p-7 text-center space-y-4">
            <div className="w-16 h-16 bg-teal-50 text-[#067978] rounded-full flex items-center justify-center mx-auto">
              <CheckCircle2 size={36} />
            </div>
            <h2 className="font-bold text-lg text-slate-900">Downgrade Request Received</h2>
            <p className="text-xs text-slate-600 leading-relaxed max-w-sm mx-auto">
              {requestSuccessMessage}
            </p>
            <button
              onClick={() => {
                setShowDowngradeModal(false);
                setSelectedDowngradePlan(null);
                setRequestSuccessMessage(null);
              }}
              className="w-full py-2.5 bg-[#067978] hover:bg-[#05605f] text-white text-xs font-bold rounded-xl shadow-md shadow-[#067978]/20 transition-all cursor-pointer"
            >
              Got It
            </button>
          </div>
        ) : (
          <>
            <div className="p-6 pb-4 flex justify-between items-start border-b border-slate-100">
              <div>
                <h2 className="font-bold text-base text-slate-900">Request downgrade to {selectedDowngradePlan.name}</h2>
                <p className="text-xs text-slate-500 mt-0.5">Your request will be processed by our team.</p>
              </div>
              <button onClick={() => setShowDowngradeModal(false)} className="p-1 hover:bg-slate-100 rounded-lg text-slate-400 cursor-pointer">
                <X size={18} />
              </button>
            </div>
            <div className="p-6 pt-4 space-y-4">
              <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-4 space-y-2">
                <div className="flex justify-between items-center text-xs">
                  <span className="text-slate-500 font-normal">Target plan</span>
                  <b className="font-semibold text-slate-800">{selectedDowngradePlan.name} Tier</b>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className="text-slate-500 font-normal">Pricing</span>
                  <b className="font-semibold text-[#067978]">{formatCurrency(selectedDowngradePlan.monthly_price)} / month + 18% GST</b>
                </div>
              </div>

              <div>
                <CustomSelect
                  label="Reason for downgrading"
                  value={requestReason}
                  onChange={(val) => setRequestReason(val)}
                  options={downgradeReasonOptions}
                />
              </div>

              <div className="space-y-1.5">
                <label className="block text-xs font-bold text-slate-700 tracking-wide mb-1.5">Comments (optional)</label>
                <textarea
                  rows={2}
                  value={requestComments}
                  onChange={(e) => setRequestComments(e.target.value)}
                  placeholder="Any specific note for the team"
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm font-normal text-slate-700 transition-all outline-none focus:border-primary focus:ring-1 focus:ring-primary resize-none shadow-sm"
                />
              </div>

              <div className="flex gap-3 justify-end pt-2 border-t border-slate-100">
                <button
                  onClick={() => setShowDowngradeModal(false)}
                  className="px-4 py-2 border border-slate-200 hover:bg-slate-50 text-xs font-bold text-slate-600 rounded-xl cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDowngradeRequest}
                  disabled={isSubmittingRequest}
                  className="px-4 py-2 bg-[#067978] hover:bg-[#05605f] text-white text-xs font-bold rounded-xl transition-all shadow-md shadow-[#067978]/20 flex items-center gap-1.5 cursor-pointer disabled:opacity-60"
                >
                  {isSubmittingRequest ? (
                    <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <span>Submit Downgrade Request</span>
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DowngradeModal;
